// useChat.jsx

import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { socket } from '../api';
import * as chatApi from '../api';

export const useChat = (user) => {
    const queryClient = useQueryClient();
    const [typingUsers, setTypingUsers] = useState({});

    const { data: chats, isLoading: isChatsLoading } = useQuery({
        queryKey: ['chats', user?.id],
        queryFn: () => chatApi.getChatList(user.id),
        enabled: !!user,
    });

    const startTyping = useCallback(({ receiverId, teamId }) => {
        if (teamId) {
            socket.emit('start_typing', { teamId });
        } else if (receiverId) {
            socket.emit('start_typing', { receiverId });
        }
    }, []);

    const stopTyping = useCallback(({ receiverId, teamId }) => {
        if (teamId) {
            socket.emit('stop_typing', { teamId });
        } else if (receiverId) {
            socket.emit('stop_typing', { receiverId });
        }
    }, []);

    useEffect(() => {
        if (!user) return;

        socket.emit('joinRoom', user.id);
        
        // FIX: Mentor joins all their assigned team rooms.
        // A regular user only joins their single team room.
        if (user.role === 'mentor') {
            const fetchMentorTeams = async () => {
                const teams = await chatApi.fetchTeamsByMentor(user.id);
                if (teams) {
                    teams.forEach(team => {
                        socket.emit('joinTeamRoom', team.id);
                    });
                }
            };
            fetchMentorTeams();
        } else if (user.team?.id) {
            socket.emit('joinTeamRoom', user.team.id);
        }

        const handleNewMessage = (newMessage) => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
            const queryKey = newMessage.teamId
                ? ['messages', 'team', newMessage.teamId]
                : ['messages', 'user', newMessage.sender.id === user.id ? newMessage.receiver.id : newMessage.sender.id];
            queryClient.invalidateQueries({ queryKey });
        };

        const handleUserTyping = ({ senderId, teamId, senderName }) => {
            // FIX: Don't show typing for the current user
            if (senderId === user.id) return;

            const key = teamId || senderId;
            setTypingUsers(prev => ({ ...prev, [key]: senderName || true }));
        };

        const handleUserStoppedTyping = ({ senderId, teamId }) => {
            // FIX: Don't show typing for the current user
            if (senderId === user.id) return;
            
            const key = teamId || senderId;
            setTypingUsers(prev => {
                const newTyping = { ...prev };
                delete newTyping[key];
                return newTyping;
            });
        };

        const handleMessagesRead = ({ messageIds, chatId }) => {
            queryClient.invalidateQueries(['messages', 'user', chatId]);
            queryClient.invalidateQueries(['messages', 'team', chatId]);
        };

        const handleUserStatusUpdate = ({ userId, status, lastSeen }) => {
            queryClient.setQueryData(['chats', user.id], (oldData) => {
                if (!oldData) return [];
                return oldData.map(chat => {
                    if (chat.type === 'individual' && chat.participant.id === userId) {
                        return {
                            ...chat,
                            participant: {
                                ...chat.participant,
                                status: status,
                                lastSeen: lastSeen,
                            }
                        };
                    }
                    return chat;
                });
            });
        };

        socket.on('newMessage', handleNewMessage);
        socket.on('user_is_typing', handleUserTyping);
        socket.on('user_stopped_typing', handleUserStoppedTyping);
        socket.on('messages_were_read', handleMessagesRead);
        socket.on('userStatusUpdate', handleUserStatusUpdate);

        return () => {
            socket.off('newMessage', handleNewMessage);
            socket.off('user_is_typing', handleUserTyping);
            socket.off('user_stopped_typing', handleUserStoppedTyping);
            socket.off('messages_were_read', handleMessagesRead);
            socket.off('userStatusUpdate', handleUserStatusUpdate);
        };
    }, [user, queryClient]);

    const sendMessage = useMutation({
        mutationFn: chatApi.sendMessage,
        onSuccess: ({ isTeamChat, chatId }) => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
            const queryKey = isTeamChat ? ['messages', 'team', chatId] : ['messages', 'user', chatId];
            queryClient.invalidateQueries({ queryKey });
        },
    });

    // FIX: Add a new mutation for marking team messages as read
    const markTeamMessagesAsReadMutation = useMutation({
        mutationFn: chatApi.markTeamMessagesAsRead,
        onSuccess: ({ teamId }) => {
            // Invalidate the specific team chat to force a re-fetch and update the UI
            queryClient.invalidateQueries(['messages', 'team', teamId]);
        },
    });
    
    // FIX: Add a new mutation for marking individual messages as read
    const markMessagesAsReadMutation = useMutation({
        mutationFn: chatApi.markMessagesAsRead,
        onSuccess: ({ chatPartnerId }) => {
            // Invalidate the specific individual chat to force a re-fetch and update the UI
            queryClient.invalidateQueries(['messages', 'user', chatPartnerId]);
        },
    });

    const uploadImage = useMutation({
        mutationFn: chatApi.uploadImageFile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
        },
    });

    const uploadDocument = useMutation({
        mutationFn: chatApi.uploadDocumentFile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
        },
    });

    const uploadModel = useMutation({
        mutationFn: chatApi.uploadModelFile,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
        },
    });

    const deleteMessage = useMutation({
        mutationFn: chatApi.deleteMessage,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['chats'] });
        },
    });
    
    return {
        chats,
        isChatsLoading,
        typingUsers,
        sendMessage,
        uploadImage,
        uploadDocument,
        uploadModel,
        deleteMessage,
        startTyping,
        stopTyping,
        markTeamMessagesAsRead: markTeamMessagesAsReadMutation,
        markMessagesAsRead: markMessagesAsReadMutation,
    };
};